<script setup lang="ts">
// The Ask tool, in the left rail beside Details and Analysis. It is the
// plain-English way to shape the view: the question becomes the shared filter
// spec (and may choose the ledger's table), so the table, the counts and the map
// all answer it together. A question that cannot be expressed leaves the view
// untouched and shows the server's explanation, keeping the question so it can
// be rephrased.
import { ref } from "vue";

import { useStoreMapReview } from "@/store/map-review";

const store = useStoreMapReview();

const question = ref("");
const asking = ref(false);
const error = ref("");
const status = ref("");

async function runAsk(): Promise<void> {
	const text = question.value.trim();
	if (text.length < 3 || asking.value) return;
	asking.value = true;
	error.value = "";
	status.value = "";
	try {
		const reply = await store.ask(text);
		if (reply.answered) {
			// The filter, the table and the map are the answer; the pane keeps the
			// form and reports that the view moved, so a second question is one
			// keystroke away.
			question.value = "";
			status.value = "Applied to the filters and the ledger.";
		} else {
			error.value = reply.explanation;
		}
	} catch {
		error.value =
			store.error ||
			"The question helper could not be reached. Please try again.";
	} finally {
		asking.value = false;
	}
}
</script>

<template>
	<div class="ask-pane">
		<p class="ask-intro">
			Ask in plain English to set up filters, switch the ledger to another
			table, or narrow the map and the records to a set. The question replaces
			the current filter spec, because it is the whole request.
		</p>

		<form class="ask-form" @submit.prevent="runAsk">
			<label class="ask-label" for="map-review-ask">Question</label>
			<input
				id="map-review-ask"
				v-model="question"
				type="text"
				class="ask-input"
				placeholder="e.g. treatments by Miguel using a biopesticide in March"
				:disabled="asking"
			/>
			<button
				type="submit"
				class="btn btn-sm btn-primary ask-submit"
				:disabled="asking || question.trim().length < 3"
			>
				<i class="bi bi-stars" aria-hidden="true"></i>
				{{ asking ? "Asking…" : "Ask" }}
			</button>
		</form>

		<p v-if="status" class="ask-status" role="status">{{ status }}</p>
		<p v-if="error" class="ask-error" role="alert">{{ error }}</p>

		<p class="ask-examples">
			Examples: “sites with an invasive-Aedes tag”, “treatments over the
			prescribed rate last month”, “observations where larvae were found”.
		</p>
	</div>
</template>

<style scoped>
.ask-pane {
	display: flex;
	flex-direction: column;
	gap: 8px;
	font-size: 0.8125rem;
}

.ask-intro,
.ask-examples {
	margin: 0;
	color: #6c757d;
	font-size: 0.75rem;
	line-height: 1.45;
}

.ask-form {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.ask-label {
	font-weight: 600;
	color: #495057;
}

.ask-input {
	width: 100%;
	height: 30px;
	padding: 3px 8px;
	font-size: 0.8125rem;
	color: #212529;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
}

.ask-submit {
	align-self: flex-start;
}

.ask-status {
	margin: 0;
	font-size: 0.75rem;
	color: #146c43;
}

.ask-error {
	margin: 0;
	font-size: 0.75rem;
	line-height: 1.4;
	color: #b3261e;
}
</style>
